/** @jsx isolateComponent */

import { Fragment } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { Link } from '@zapier/design-system';

import { trialExtensionReasons } from '@zapier/design-system-revenue/src/types';

import { NudgeImage } from '../NudgeImage';

type Props = {
  appName?: string;
  canExtendTrial: boolean;
};

export const getUpgradeReasonContent = ({
  appName,
  canExtendTrial,
}: Props) => ({
  [trialExtensionReasons.premium]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Premium App',
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premium} />
    ),
    subtitleText: 'Paid Feature',
  },

  [trialExtensionReasons.premiumLimit]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to more{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to more{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: "You've run out of Premium Apps",
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premiumLimit} />
    ),
    subtitleText: 'Paid Feature',
  },

  [trialExtensionReasons.zapLimit]: {
    bodyText: canExtendTrial
      ? "To turn on your Zap, you'll need access to more Zaps. Upgrade now or start a free 7-day trial of the Professional plan to use unlimited Zaps."
      : "To turn on your Zap, you'll need access to more Zaps. Turn off some of your other Zaps or upgrade now to turn on this Zap.",
    headingText: 'More Zaps',
    image: <NudgeImage type={trialExtensionReasons.zapLimit} />,
    subtitleText: 'Heads Up',
  },

  [trialExtensionReasons.multistep]: {
    bodyText: canExtendTrial
      ? `To create a Zap with more than one action, you'll need a paid plan. Upgrade now or start a free 7-day trial of the Professional plan to use multi-step Zaps.`
      : "To create a Zap with more than one action, you'll need a paid plan. Edit your zap or upgrade now to turn on your Zap.",
    headingText: 'Multi-step Zap',
    image: <NudgeImage type={trialExtensionReasons.multistep} />,
    subtitleText: 'Paid Feature',
  },

  [trialExtensionReasons.paths]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use Paid Features like Paths.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Paths',
    image: <NudgeImage type={trialExtensionReasons.paths} />,
    subtitleText: 'Paid Feature',
  },

  [trialExtensionReasons.emailSupport]: {
    bodyText: (
      <Fragment>
        Upgrade to email our world class Support team and we’ll help get you up
        & running! Or, get{' '}
        <Link href="/community" target="_blank">
          support from our Community
        </Link>
        , for free.
      </Fragment>
    ),
    headingText: 'Upgrade to contact Support',
    image: <NudgeImage type={trialExtensionReasons.emailSupport} />,
    subtitleText: 'Paid Feature',
  },

  [trialExtensionReasons.unknown]: {
    bodyText: "To activate your Zap, you'll need to upgrade your account.",
    headingText: 'Upgrade Needed',
  },
});
